<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="basicInfoForm">
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('provider-name') }}</label>
          <p class="label-info">
            Full Name of the Provider/Organisation offering Resources and acting as main contact
            point for the Resources.
          </p>
          <v-text-field
            v-model.trim="provider.name"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('hosting-institution') }}</label>
          <p class="label-info">
            Name of the organisation/institution legally hosting (housing) the Provider or its
            coordinating centre.
          </p>
          <v-autocomplete
            v-model="provider.hostingInstitution"
            :items="institutions"
            :item-value="'id'"
            :item-text="'name'"
            return-object
            :placeholder="$t('hosting-institution')"
            outlined
            :rules="requiredRule()"
            class="required"
          ></v-autocomplete>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('website') }}</label>
          <p class="label-info">{{ $t('website-message') }}</p>
          <v-text-field
            v-model="provider.website"
            outlined
            :placeholder="$t('website')"
            :rules="requiredRule() && checkURL()"
            class="required"
            prepend-inner-icon="mdi-link-variant"
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Optional -->
      <v-row v-if="edit">
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="mt-5">
                <v-row>
                  <v-col cols="12" xl="6" lg="6" md="6" sm="12">
                    <label>Abbreviation</label>
                    <p class="label-info">
                      Abbreviation of the Provider Name
                    </p>
                    <v-text-field
                      v-model.trim="optionalProvider.abbreviation"
                      outlined
                      :placeholder="'Abbreviation'"
                      clearable
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updateProviderBasicInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-else
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import EventBus from '@/helpers/event-bus.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'AddProviderBasicInfo',
  components: { AlertUnsavedChanges },
  mixins: [util],
  data() {
    return {
      provider: {
        name: '',
        hostingInstitution: null,
        website: '',
      },
      optionalProvider: {
        abbreviation: '',
      },
      institutions: [],
      isSubmitted: false,
      unsavedChanges: false,
      initialProvider: {},
      initialOptionalProvider: {},
      dataLoaded: false,
    };
  },
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    provider: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.provider.name !== this.initialProvider.name ||
          this.provider.hostingInstitution !== this.initialProvider.hostingInstitution ||
          this.provider.website !== this.initialProvider.website;
      },
    },
    optionalProvider: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.optionalProvider.abbreviation !== this.initialOptionalProvider.abbreviation;
      },
    },
  },
  mounted() {
    this.axios.get('/api/v1/hosting-institutions/all').then(res => (this.institutions = res.data));

    if (this.edit) {
      this.getProvider();
    }
  },
  methods: {
    next() {
      const isFormValid = this.$refs.basicInfoForm.validate();
      if (isFormValid && this.provider.hostingInstitution.id) {
        const { name, hostingInstitution, website } = this.provider;

        const providerBasicInfo = {
          name,
          hostingInstitution: {
            id: hostingInstitution.id,
            name: hostingInstitution.name,
          },
          website,
        };

        this.$emit('tab', 1);
        this.$emit('providerBasicInfo', providerBasicInfo);
      }
    },
    getProvider() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          console.log('Get Provider Basic Info', res.data);

          this.provider.name = res.data.name;
          this.provider.hostingInstitution = res.data.hostingInstitution;
          this.provider.website = res.data.website;
          this.optionalProvider.abbreviation = res.data.abbreviation;

          this.initialProvider = {
            name: res.data.name,
            hostingInstitution: res.data.hostingInstitution,
            website: res.data.website,
          };
          this.initialOptionalProvider = {
            abbreviation: res.data.abbreviation,
          };

          this.dataLoaded = true;
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    deleteEmptyProperties(obj) {
      if (!obj || typeof obj !== 'object') {
        return;
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          if (item === null || item === undefined || item === '') {
            obj.splice(index, 1);
          } else if (typeof item === 'object') {
            this.deleteEmptyProperties(item);
            if (Object.keys(item).length === 0) {
              obj.splice(index, 1);
            }
          }
        });
      } else {
        for (const key in obj) {
          if (obj[key] === null || obj[key] === undefined || obj[key] === '') {
            delete obj[key];
          } else if (Array.isArray(obj[key])) {
            this.deleteEmptyProperties(obj[key]);
            if (obj[key].length === 0) {
              delete obj[key];
            }
          } else if (typeof obj[key] === 'object') {
            this.deleteEmptyProperties(obj[key]);
            if (Object.keys(obj[key]).length === 0) {
              delete obj[key];
            }
          }
        }
      }
    },
    cancel() {
      this.getProvider();
    },
    updateProviderBasicInfo() {
      const id = this.$route.params.id;

      const providerUpdateData = {
        id: id,
        name: this.provider.name,
        hostingInstitution: this.provider.hostingInstitution,
        website: this.provider.website,
        abbreviation: this.optionalProvider.abbreviation,
      };

      this.deleteEmptyProperties(providerUpdateData);

      const isFormValid = this.$refs.basicInfoForm.validate();
      if (isFormValid) {
        this.axios
          .post('/api/v1/provider-settings/basic-info', providerUpdateData)
          .then(response => {
            console.log('Provider info updated successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Provider Updated Successfully',
              color: 'primary200',
            });
            this.initialProvider = { ...this.provider };
            this.initialOptionalProvider = { ...this.optionalProvider };
            this.unsavedChanges = false;
            this.$emit('updatedChanges', true);
          })
          .catch(error => {
            console.error('Error updating provider info:', error);
          });
      }
    },
  },
};
</script>
